import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DataTable,
  PageHeader,
  StatsGrid,
  useDataTable,
  type DataTableColumn,
} from "@/components/shared";
import { ExportMenu, exportToExcel, exportToPdf, reportFileName, type ExportColumn } from "@/components/reports";
import type { ChannelOrder } from "@/services/channelOrdersService";

const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const inr = (value: number) => `₹${Number(value).toLocaleString("en-IN")}`;
const amountOf = (order: ChannelOrder) => order.totalAmount ?? order.quantity * order.unitPrice;

const statusTone: Record<string, string> = {
  Approved: "bg-success/10 text-success border-success/20",
  Pending: "bg-warning/10 text-warning border-warning/20",
  Rejected: "bg-destructive/10 text-destructive border-destructive/20",
};

const columns: DataTableColumn<ChannelOrder>[] = [
  {
    id: "productName",
    header: "Product",
    sortable: true,
    mobilePriority: 1,
    cell: (row) => (
      <div className="min-w-0">
        <p className="truncate font-semibold">{row.productName}</p>
        <p className="truncate text-xs text-muted-foreground">
          {row.buyerName} → {row.sellerName}
        </p>
      </div>
    ),
  },
  { id: "quantity", header: "Qty", sortable: true, align: "right", mobileLabel: "Qty", mobilePriority: 2, cell: (row) => row.quantity },
  { id: "totalAmount", header: "Amount", sortable: true, align: "right", mobileLabel: "Amount", mobilePriority: 3, cell: (row) => <span className="font-semibold">{inr(amountOf(row))}</span> },
  { id: "commissionAmount", header: "Commission", sortable: true, align: "right", hideOnMobile: true, cell: (row) => inr(row.commissionAmount) },
  { id: "businessVolume", header: "BV", sortable: true, align: "right", hideOnMobile: true, cell: (row) => row.businessVolume ?? 0 },
  {
    id: "paymentMethodText",
    header: "Payment",
    mobileLabel: "Payment",
    mobilePriority: 4,
    cell: (row) => (
      <div className="text-xs">
        <p>{row.paymentMethodText}</p>
        <p className="text-muted-foreground">{row.paymentStatusText}</p>
      </div>
    ),
  },
  {
    id: "statusText",
    header: "Status",
    sortable: true,
    mobileLabel: "Status",
    mobilePriority: 5,
    cell: (row) => (
      <Badge variant="outline" className={statusTone[row.statusText] ?? ""}>
        {row.statusText}
      </Badge>
    ),
  },
  { id: "createdAt", header: "Date", sortable: true, hideOnMobile: true, className: "text-sm text-muted-foreground", cell: (row) => formatDate(row.createdAt) },
];

const exportColumns: ExportColumn<ChannelOrder>[] = [
  { header: "Date", value: (r) => formatDate(r.createdAt), width: 14 },
  { header: "Product", value: (r) => r.productName, width: 28 },
  { header: "SKU", value: (r) => r.productSku || "", width: 14 },
  { header: "Buyer", value: (r) => r.buyerName, width: 22 },
  { header: "Seller", value: (r) => r.sellerName, width: 22 },
  { header: "Qty", value: (r) => r.quantity, numeric: true, width: 8 },
  { header: "Amount (INR)", value: (r) => Number(amountOf(r)), numeric: true, width: 14 },
  { header: "Commission (INR)", value: (r) => Number(r.commissionAmount), numeric: true, width: 16 },
  { header: "BV", value: (r) => Number(r.businessVolume ?? 0), numeric: true, width: 10 },
  { header: "Payment", value: (r) => r.paymentMethodText, width: 12 },
  { header: "Payment status", value: (r) => r.paymentStatusText, width: 14 },
  { header: "Status", value: (r) => r.statusText, width: 12 },
];

interface ChannelOrdersReportProps {
  title: string;
  description: string;
  fileName: string;
  orders: ChannelOrder[];
  isLoading: boolean;
  /** Compact stat cards shown above the table. */
  stats: ReactNode;
  statColumns?: 2 | 3 | 4;
}

/** One distributor/wholesaler report: stat strip, searchable table of orders, Excel/PDF export. */
const ChannelOrdersReport = ({ title, description, fileName, orders, isLoading, stats, statColumns = 4 }: ChannelOrdersReportProps) => {
  const table = useDataTable<ChannelOrder>({
    data: orders,
    searchKeys: ["productName", "buyerName", "sellerName", "statusText", "paymentMethodText"],
  });

  const rows = table.allRows;
  const options = () => ({
    title,
    fileName: reportFileName(fileName),
    columns: exportColumns,
    rows,
    summary: [
      { label: "Orders", value: String(rows.length) },
      { label: "Total amount (INR)", value: rows.reduce((sum, r) => sum + Number(amountOf(r)), 0).toLocaleString("en-IN", { minimumFractionDigits: 2 }) },
    ],
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-56" />
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={title}
        description={description}
        actions={
          <ExportMenu disabled={rows.length === 0} onExcel={() => exportToExcel(options())} onPdf={() => exportToPdf(options())} />
        }
      />
      <StatsGrid columns={statColumns}>{stats}</StatsGrid>
      <DataTable
        title={`${title} orders (${rows.length})`}
        columns={columns}
        data={table.rows}
        getRowId={(row) => String(row.id)}
        search={table.state.search}
        onSearchChange={table.setSearch}
        searchPlaceholder="Search product, party, status…"
        sort={table.state.sort}
        onSortChange={table.toggleSort}
        pagination={table.pagination}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
        emptyMessage="No orders yet."
      />
    </div>
  );
};

export default ChannelOrdersReport;
